import { createFileRoute } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useState } from 'react'
import PageHeader from '@/components/settings/PageHeader'
import { Button } from '@/components/shared/atoms/Button'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useSendPasswordResetEmail } from '@/hooks/api/useAuth'

export const Route = createFileRoute('/settings/account/change-password')({
    component: RouteComponent,
})

function RouteComponent() {
    return <ChangePassword />
}

const ChangePassword = () => {
    const { user } = useCurrentUser()
    const [emailSent, setEmailSent] = useState(false)
    const {
        mutate: sendResetEmail,
        isPending: isSending,
        error,
    } = useSendPasswordResetEmail()

    const handleSendResetEmail = () => {
        if (!user?.email) return

        sendResetEmail(
            { email: user.email },
            {
                onSuccess: () => {
                    setEmailSent(true)
                },
            }
        )
    }

    if (emailSent) {
        return (
            <div className="flex flex-col px-2">
                <PageHeader title="ChangePassword" />
                <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
                    <div className="mb-6">
                        <img
                            src={assetPath('icons/status/success-blue.svg')}
                            alt="Success"
                            className="mx-auto mb-4 h-16 w-16"
                        />
                        <h2 className="text-18 mb-2 font-semibold">
                            Reset Email Sent!
                        </h2>
                        <p className="text-14 text-black-48">
                            We've sent a password reset link to{' '}
                            <span className="font-medium">{user?.email}</span>
                        </p>
                        <p className="text-12 text-black-48 mt-2">
                            Check your email and follow the instructions to
                            reset your password.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="mx-auto flex flex-col px-2">
            <PageHeader title="Change Password" />
            <div className="mx-auto flex max-w-md flex-col gap-6 py-4">
                <div className="flex flex-col gap-4">
                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                        <div className="flex items-start gap-3">
                            <img
                                src={assetPath('icons/circle-info.svg')}
                                alt="Info"
                                className="mt-0.5 h-5 w-5 flex-shrink-0"
                            />
                            <div>
                                <h3 className="text-14 mb-1 font-semibold text-blue-900">
                                    Secure Password Reset
                                </h3>
                                <p className="text-12 text-blue-800">
                                    For your security, we'll send a password
                                    reset link to your email. This ensures only
                                    you can change your password.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <label className="text-black-48 text-14 mb-2 block">
                                Reset email will be sent to:
                            </label>
                            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                                <p className="text-14 font-medium">
                                    {user?.email}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                        <p className="text-12 text-red-800">
                            {(error as Error).message}
                        </p>
                    </div>
                )}

                <div className="mt-auto">
                    <Button
                        fullWidth
                        onClick={handleSendResetEmail}
                        disabled={isSending || !user?.email}
                        className="rounded-xl bg-black py-4 text-white"
                    >
                        {isSending
                            ? 'Sending Reset Email...'
                            : 'Send Password Reset Email'}
                    </Button>
                </div>
            </div>
        </div>
    )
}
